import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    ApiClientService,
    CachedValuesService,
    PermissionService,
    ToastService,
    RightPanelService
} from '@services/index';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { Align, Permission } from 'app/enums';
import { SortType } from 'app/types';
import { LocationModel } from 'app/types/location.type';
import { Filter, FilterForm } from './location-list.model';
import { BehaviorSubject, EMPTY, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { GetLocationsResponse } from '@services/api-client/responses';
import { sortStrings } from '@helpers/sort-string-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ModalHelper } from '@helpers/modal-helper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CreateAndUpdateLocationModalComponent } from '../../components/create-and-update-location-modal/create-and-update-location-modal.component';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'app-location-list',
    templateUrl: './location-list.component.html',
    styleUrl: './location-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LocationListComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly tablePreferencesService = inject(TablePreferencesService);

    private static readonly tableId = 'location-list' as const;

    public readonly align = Align;
    public sortKey: keyof LocationModel = 'name';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public loadingLocationIds: string[] = [];
    public permissions = Permission;
    public locationPermissions: Permission[] = this.permissionService.userPermissions;
    public readonly isUnrestricted = this.permissionService.isUnrestricted();
    private readonly updateSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public update$: Observable<void> = this.updateSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly sortSubject: BehaviorSubject<SortType<LocationModel>> = new BehaviorSubject({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<LocationModel>> = this.sortSubject.asObservable();
    public locations$: Observable<GetLocationsResponse>;
    private readonly deletedLocationIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedLocationIds$: Observable<string[]> = this.deletedLocationIdsSubject.asObservable();

    public get f() {
        return this.filterForm.controls;
    }

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(LocationListComponent.tableId)?.sort;
        if (saved) {
            this.sortKey = saved.key as keyof LocationModel;
            this.sortAscending = saved.ascending;
            this.sortSubject.next({ key: this.sortKey, ascending: this.sortAscending });
        }

        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.locations$ = this.update$.pipe(
            tap(() => {
                this.deletedLocationIdsSubject.next([]);
            }),
            switchMap(() =>
                this.apiClientService.getLocations$().pipe(
                    switchMap((location: GetLocationsResponse) =>
                        this.deletedLocationIds$.pipe(
                            map((deletedLocationIds) => location.filter((l) => !deletedLocationIds.includes(l.id)))
                        )
                    ),
                    switchMap((locations: GetLocationsResponse) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(locations);
                            })
                        )
                    ),
                    switchMap((locations: GetLocationsResponse) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                return of(sortStrings<LocationModel>(locations, sort.key, sort.ascending));
                            })
                        )
                    )
                )
            )
        );

        this.filterForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.searchSubject.next();
        });
    }

    private filterResponse(locations: LocationModel[]): LocationModel[] {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase().trim();

        return locations.filter((location) => {
            return location.name.toLowerCase().includes(searchPhrase);
        });
    }

    public openCreateLocationModal(location?: LocationModel) {
        if (this.locationPermissions.includes(this.permissions.WriteLocation)) {
            const title = this.translateService.instant('MENU.LOCATION') as string;

            this.rightPanelService.showComponent(
                title,
                CreateAndUpdateLocationModalComponent,
                { editLocation: location },
                true,
                false,
                false,
                'location_on'
            );

            this.rightPanelService.isVisible$
                .pipe(
                    filter((visible: boolean) => !visible),
                    take(1),
                    untilDestroyed(this)
                )
                .subscribe(() => {
                    this.cachedValuesService.invalidateCache();
                    this.updateSubject.next();
                });
        }
    }

    public onSortChange(key: keyof LocationModel, ascending: boolean) {
        this.tablePreferencesService.setPreferences(LocationListComponent.tableId, {
            sort: { key: key as string, ascending }
        });
        this.sortSubject.next({ key, ascending });
    }

    public confirmDeleteLocation(event: Event, location: LocationModel) {
        event.stopPropagation();
        if (this.loadingLocationIds.includes(location.id)) {
            return;
        }

        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.LOCATION.TITLES.DELETE_LOCATION',
            bodyLanguageKey: 'FEATURES.LOCATION.TEXTS.CONFIRM_DELETE_LOCATION',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.loadingLocationIds.push(location.id);
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteLocation$({ locationId: location.id });
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingLocationIds = this.loadingLocationIds.filter((id) => id !== location.id);
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.LOCATION.TOAST_MESSAGES.DELETE_LOCATION_SUCCESS'
                    ) as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedLocationIdsSubject.next([...this.deletedLocationIdsSubject.value, location.id]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.LOCATION.TOAST_MESSAGES.DELETE_LOCATION_ERROR'
                    ) as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }
}
